import { Controller } from "@hotwired/stimulus"
import { createPopper } from "@popperjs/core"

const OPEN_DELAY = 180
const CLOSE_DELAY = 250
const VIEWPORT_PADDING = 12

/**
 * Discloses the client's full ranked talent list from the compact domain-mix
 * badge. The panel opens on hover, pins on click, and is dismissed with the
 * close button, Escape, or an outside click.
 */
export default class extends Controller {
  static targets = ["trigger", "panel"]

  connect() {
    this.pinned = false
    this.pointerInside = false
    this.openTimer = null
    this.closeTimer = null
    this.popper = null

    this.onDocumentPointerDown = this.handleDocumentPointerDown.bind(this)
    this.onDocumentKeydown = this.handleDocumentKeydown.bind(this)
    this.onBeforeCache = this.close.bind(this)

    document.addEventListener("turbo:before-cache", this.onBeforeCache)

    // A restored or re-rendered badge always starts closed.
    this.close()
  }

  disconnect() {
    this.close()
    document.removeEventListener("turbo:before-cache", this.onBeforeCache)
  }

  // --- Pointer -------------------------------------------------------------

  pointerEnter() {
    this.pointerInside = true
    this.cancelClose()
    if (this.isOpen) return

    this.cancelOpen()
    this.openTimer = setTimeout(() => this.open(), OPEN_DELAY)
  }

  pointerLeave() {
    this.pointerInside = false
    this.cancelOpen()
    this.scheduleClose()
  }

  // --- Trigger and dismissal ----------------------------------------------

  toggle(event) {
    event.preventDefault()
    this.cancelOpen()
    this.cancelClose()

    if (this.pinned) {
      this.close()
      return
    }

    this.open()
    this.pinned = true
  }

  close() {
    this.cancelOpen()
    this.cancelClose()
    this.pinned = false

    if (this.hasPanelTarget) this.panelTarget.hidden = true
    if (this.hasTriggerTarget) this.triggerTarget.setAttribute("aria-expanded", "false")

    document.removeEventListener("pointerdown", this.onDocumentPointerDown)
    document.removeEventListener("keydown", this.onDocumentKeydown)

    if (this.popper) {
      this.popper.destroy()
      this.popper = null
    }
  }

  // Dismissal that should hand focus back to the trigger.
  closeAndRefocus() {
    const hadFocus = this.element.contains(document.activeElement)
    this.close()
    if (hadFocus && this.hasTriggerTarget) this.triggerTarget.focus()
  }

  focusOut(event) {
    if (this.pinned || this.pointerInside) return
    if (this.element.contains(event.relatedTarget)) return

    this.close()
  }

  // --- Internals -----------------------------------------------------------

  get isOpen() {
    return this.hasPanelTarget && !this.panelTarget.hidden
  }

  open() {
    this.cancelOpen()
    if (!this.hasPanelTarget || !this.hasTriggerTarget) return

    this.panelTarget.hidden = false
    this.triggerTarget.setAttribute("aria-expanded", "true")

    document.addEventListener("pointerdown", this.onDocumentPointerDown)
    document.addEventListener("keydown", this.onDocumentKeydown)

    if (this.popper) {
      this.popper.update()
      return
    }

    // A fixed strategy keeps the panel clear of the cockpit header's overflow.
    this.popper = createPopper(this.triggerTarget, this.panelTarget, {
      placement: "bottom-start",
      strategy: "fixed",
      modifiers: [
        { name: "offset", options: { offset: [0, 8] } },
        { name: "flip", options: { padding: VIEWPORT_PADDING } },
        { name: "preventOverflow", options: { padding: VIEWPORT_PADDING } },
      ],
    })
  }

  scheduleClose() {
    if (this.pinned || !this.isOpen) return
    if (this.element.contains(document.activeElement)) return

    this.cancelClose()
    this.closeTimer = setTimeout(() => {
      if (this.pinned || this.pointerInside) return
      if (this.element.contains(document.activeElement)) return

      this.close()
    }, CLOSE_DELAY)
  }

  cancelOpen() {
    if (this.openTimer) clearTimeout(this.openTimer)
    this.openTimer = null
  }

  cancelClose() {
    if (this.closeTimer) clearTimeout(this.closeTimer)
    this.closeTimer = null
  }

  handleDocumentPointerDown(event) {
    if (this.element.contains(event.target)) return

    // An outside click must not steal focus from its destination.
    this.close()
  }

  handleDocumentKeydown(event) {
    if (event.key !== "Escape") return

    event.preventDefault()
    this.closeAndRefocus()
  }
}
